/**
 * Site Configuration
 * 
 * Central configuration file for managing page visibility and SEO settings.
 * 
 * For each page:
 * - enabled: true/false - Controls whether the page is accessible on the website
 *   (When false, the page won't render and links to it will be hidden)
 * - seoEnabled: true/false - Controls whether SEO meta tags are rendered
 *   (When false, the page will have noindex, nofollow and won't appear in sitemap)
 * 
 * Usage:
 * - To disable a page entirely: Set enabled: false
 * - To hide a page from search engines but keep it accessible: Set seoEnabled: false
 * - To enable both: Set both to true (default)
 */

export const pagesConfig = {
  home: {
    enabled: true,
    seoEnabled: true,
    path: '/',
    title: 'Home',
    showInNav: false, // Home is usually accessed via logo
    showInFooter: false,
  },
  products: {
    enabled: true,
    seoEnabled: true,
    path: '/products',
    title: 'Products',
    showInNav: true,
    showInFooter: true,
  },
  about: {
    enabled: true,
    seoEnabled: true,
    path: '/about',
    title: 'About Us',
    showInNav: true,
    showInFooter: true,
  },
  quality: {
    enabled: true,
    seoEnabled: true,
    path: '/quality',
    title: 'Quality & Compliance',
    showInNav: true,
    showInFooter: true,
  },
  contact: {
    enabled: true,
    seoEnabled: true,
    path: '/contact',
    title: 'Contact',
    showInNav: true,
    showInFooter: true,
  },
  disclaimer: {
    enabled: true,
    seoEnabled: true,
    path: '/disclaimer',
    title: 'Disclaimer',
    showInNav: false,
    showInFooter: true,
    isLegal: true,
  },
  privacy: {
    enabled: true,
    seoEnabled: true,
    path: '/privacy',
    title: 'Privacy Policy',
    showInNav: false,
    showInFooter: true,
    isLegal: true,
  },
  terms: {
    enabled: true,
    seoEnabled: true,
    path: '/terms',
    title: 'Terms of Use',
    showInNav: false,
    showInFooter: true,
    isLegal: true,
  },
}

/**
 * Product SEO Configuration
 * 
 * Default settings for all products. Individual products can override these
 * using the `enabled` and `seoEnabled` flags in products.js
 * 
 * In products.js, add these optional fields to any product:
 * - enabled: false - Hides the product from the website entirely
 * - seoEnabled: false - Hides the product from search engines but keeps it accessible
 */
export const productDefaults = {
  enabled: true,
  seoEnabled: true,
}

/**
 * Global SEO Settings
 */
export const seoConfig = {
  siteUrl: 'https://sibrlifescience.com',
  siteName: 'SIBR Lifescience',
  defaultImage: '/assets/images/logo.png',
  twitterHandle: '',
  // Set to false to disable all SEO across the site (useful for staging)
  globalSeoEnabled: true,
}

// Helper functions
export const isPageEnabled = (pageKey) => {
  return pagesConfig[pageKey]?.enabled ?? true
}

export const isPageSeoEnabled = (pageKey) => {
  if (!seoConfig.globalSeoEnabled) return false
  return pagesConfig[pageKey]?.seoEnabled ?? true
}

export const getEnabledNavPages = () => {
  return Object.entries(pagesConfig)
    .filter(([, config]) => config.enabled && config.showInNav)
    .map(([key, config]) => ({ key, ...config }))
}

export const getEnabledFooterPages = () => {
  return Object.entries(pagesConfig)
    .filter(([, config]) => config.enabled && config.showInFooter)
    .map(([key, config]) => ({ key, ...config }))
}

export const getEnabledLegalPages = () => {
  return Object.entries(pagesConfig)
    .filter(([, config]) => config.enabled && config.isLegal)
    .map(([key, config]) => ({ key, ...config }))
}

export const getEnabledQuickLinkPages = () => {
  return Object.entries(pagesConfig)
    .filter(([, config]) => config.enabled && config.showInFooter && !config.isLegal)
    .map(([key, config]) => ({ key, ...config }))
}

// For sitemap generation - returns all SEO-enabled pages
export const getSeoEnabledPages = () => {
  if (!seoConfig.globalSeoEnabled) return []
  return Object.entries(pagesConfig)
    .filter(([, config]) => config.enabled && config.seoEnabled)
    .map(([key, config]) => ({ key, ...config }))
}

